import React, { useState, useRef } from "react";
import styled from "styled-components";
import { Button, Tooltip, Modal, Form, Input, notification } from "antd";
import {
    PlusOutlined,
    CaretUpOutlined,
    CaretDownOutlined,
    EditOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import {
    getServices,
    changeServiceOrder,
    createServices,
    updateServiceCategory,
    deleteServiceCategory,
} from "../../../service";
import { colors, spacing, sizes, transitions } from "../../../styles/theme";
import { PriceInput } from "../../atoms/PriceInput";
import { EditServiceModal } from "../edit-service-modal/EditServiceModal";
import { useBranchContext } from "../../../contexts/branch-context";
import { currencySymbol } from "../../../utils/currency";

const CategoryContainer = styled.div`
  background: ${colors.gray100};
  border-radius: ${sizes.borderRadius};
  border: 1px solid ${colors.gray200};
  padding: 0;
  overflow: hidden;

  @media (max-width: 768px) {
    border-radius: ${spacing.md};
  }
`;

const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 80px;
  padding: 0 ${spacing.xxxl};
  background: ${colors.gray100};
  border-radius: ${sizes.borderRadius} ${sizes.borderRadius} 0 0;

  @media (max-width: 768px) {
    padding: 0 ${spacing.md};
    border-radius: ${spacing.md} ${spacing.md} 0 0;
  }
`;

const CategoryTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
`;

const CategoryTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.textPrimary};
  margin: 0;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: ${colors.primary};
  }

  @media (max-width: 768px) {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.4;
  }
`;

const CategoryActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xl};
`;

const OrderButtonsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.lg};

  @media (max-width: 768px) {
    display: none;
  }
`;

const ArrowButton = styled(Button)`
  
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    &.edit-button {
      display: none;
    }
  }
`;

const ServicesList = styled.div<{ hasScroll?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 0 ${spacing.lg} ${spacing.lg} ${spacing.lg};
  ${props => props.hasScroll && `
    max-height: calc(4.5 * 80px + ${spacing.lg});
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: ${colors.gray50};
    }

    &::-webkit-scrollbar-thumb {
      background: ${colors.gray300};
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: ${colors.gray400};
    }
  `}

  @media (max-width: 768px) {
    padding: 0 ${spacing.md} ${spacing.md} ${spacing.md};
  }
`;

const ServiceRow = styled.div`
  background: ${colors.white};
  border-bottom: 1px solid ${colors.borderLight};
  padding: 0 ${spacing.lg};
  height: 80px;
  min-height: 80px;
  max-height: 80px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: ${transitions.normal};
  cursor: pointer;

  &:first-child {
    border-radius: ${sizes.borderRadius} ${sizes.borderRadius} 0 0;
  }

  &:last-child {
    border-bottom: none;
    border-radius: 0 0 ${sizes.borderRadius} ${sizes.borderRadius};
  }

  &:first-child:last-child {
    border-radius: ${sizes.borderRadius};
  }

  &:hover {
    background: ${colors.gray200};
  }

  @media (max-width: 768px) {
    padding: 0 ${spacing.md};

    &:first-child {
      border-radius: ${spacing.md} ${spacing.md} 0 0;
    }

    &:last-child {
      border-radius: 0 0 ${spacing.md} ${spacing.md};
    }

    &:first-child:last-child {
      border-radius: ${spacing.md};
    }
  }
`;

const ServiceInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  flex: 1;
`;

const ServiceImage = styled.img`
  width: 48px;
  height: 48px;
  border-radius: ${sizes.borderRadiusSmall};
  object-fit: cover;
  background: ${colors.gray100};

  @media (max-width: 768px) {
    display: none;
  }
`;

const ServiceImagePlaceholder = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${sizes.borderRadiusSmall};
  background: ${colors.gray100};

  @media (max-width: 768px) {
    display: none;
  }
`;

const ServiceName = styled.span`
  font-size: 14px;
  color: ${colors.textPrimary};
  font-weight: 500;

  @media (max-width: 768px) {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.4;
  }
`;

const ServicePrice = styled.span`
  font-size: 13px;
  color: ${colors.textSecondary};
  margin-left: 8px;
  white-space: nowrap;
`;

const ServiceActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xl};
  justify-content: flex-end;
`;

const ServiceOrderButtonsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.lg};

  @media (max-width: 768px) {
    display: none;
  }
`;

const AddButtonText = styled.span`
  .mobile-text {
    display: none;
  }

  @media (max-width: 768px) {
    .desktop-text {
      display: none;
    }
    .mobile-text {
      display: inline;
    }
  }
`;

interface ServiceCategorySectionProps {
    category: any;
    onMoveUp: () => void;
    onMoveDown: () => void;
    canMoveUp: boolean;
    canMoveDown: boolean;
}

export const ServiceCategorySection: React.FC<ServiceCategorySectionProps> = ({
    category,
    onMoveUp,
    onMoveDown,
    canMoveUp,
    canMoveDown,
}) => {
    const { t } = useTranslation();
    const { currentBranch } = useBranchContext();
    const queryClient = useQueryClient();

    const [services, setServices] = useState<any[]>([]);
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
    const [isAddServiceModalOpen, setIsAddServiceModalOpen] = useState(false);
    const [categoryForm] = Form.useForm();
    const [addServiceForm] = Form.useForm();
    const [addServicePrice, setAddServicePrice] = useState(0);
    const servicesListRef = useRef<HTMLDivElement>(null);
    const [editingServiceId, setEditingServiceId] = useState<number | null>(null);
    const [isEditServiceModalOpen, setIsEditServiceModalOpen] = useState(false);

    useQuery<any, Error>(
        [`/services-category-${category.id}`],
        () => getServices({ id: category.id }),
        {
            onSuccess(data) {
                setServices(data);
            },
        }
    );

    const { mutateAsync: changeServiceOrderMutation } = useMutation(
        changeServiceOrder,
        {
            onSuccess: (data) => {
                setServices(data);
                queryClient.invalidateQueries([`/services-category-${category.id}`]);
            },
        }
    );

    const { mutateAsync: createServiceMutation, isLoading: isCreatingService } = useMutation(
        createServices,
        {
            onSuccess: (data) => {
                queryClient.invalidateQueries([`/services-category-${category.id}`]);
                notification.success({
                    message: t("serviceCategorySection.serviceCreated"),
                    placement: "bottomRight",
                });
                addServiceForm.resetFields();
                setAddServicePrice(0);
                setIsAddServiceModalOpen(false);

                if (data && data.id) {
                    setEditingServiceId(data.id);
                    setIsEditServiceModalOpen(true);
                }

                // Scroll to end
                setTimeout(() => {
                    if (servicesListRef.current) {
                        servicesListRef.current.scrollTop = servicesListRef.current.scrollHeight;
                    }
                }, 300);
            },
            onError: () => {
                notification.error({
                    message: t("serviceCategorySection.serviceCreateError"),
                    placement: "bottomRight",
                });
            },
        }
    );

    const { mutateAsync: updateCategoryMutation } = useMutation(
        updateServiceCategory,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(["/services-categories"]);
                notification.success({
                    message: t("serviceCategorySection.categoryUpdated"),
                    placement: "bottomRight",
                });
                setIsCategoryModalOpen(false);
            },
            onError: () => {
                notification.error({
                    message: t("serviceCategorySection.categoryUpdateError"),
                    placement: "bottomRight",
                });
            },
        }
    );

    const { mutateAsync: deleteCategoryMutation } = useMutation(
        deleteServiceCategory,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(["/services-categories"]);
                notification.success({
                    message: t("serviceCategorySection.categoryDeleted"),
                    placement: "bottomRight",
                });
                setIsCategoryModalOpen(false);
            },
            onError: () => {
                notification.error({
                    message: t("serviceCategorySection.categoryDeleteError"),
                    description: t("serviceCategorySection.categoryDeleteErrorDescription"),
                    placement: "bottomRight",
                });
            },
        }
    );

    const handleMoveServiceUp = async (serviceId: number) => {
        const currentIndex = services.findIndex((s) => s.id === serviceId);
        if (currentIndex <= 0) return;

        const newServices = [...services];
        [newServices[currentIndex - 1], newServices[currentIndex]] = [
            newServices[currentIndex],
            newServices[currentIndex - 1],
        ];

        const orderedIds = newServices.map((s) => s.id);
        await changeServiceOrderMutation({
            categoryId: category.id,
            order: orderedIds,
        });
    };

    const handleMoveServiceDown = async (serviceId: number) => {
        const currentIndex = services.findIndex((s) => s.id === serviceId);
        if (currentIndex < 0 || currentIndex >= services.length - 1) return;

        const newServices = [...services];
        [newServices[currentIndex], newServices[currentIndex + 1]] = [
            newServices[currentIndex + 1],
            newServices[currentIndex],
        ];

        const orderedIds = newServices.map((s) => s.id);
        await changeServiceOrderMutation({
            categoryId: category.id,
            order: orderedIds,
        });
    };

    const handleAddService = () => {
        setIsAddServiceModalOpen(true);
    };

    const handleAddServiceSubmit = async () => {
        try {
            const values = await addServiceForm.validateFields();
            await createServiceMutation({
                ...values,
                price: addServicePrice,
                serviceCategoryId: category.id,
            });
        } catch {
            // validation failed
        }
    };

    const handleOpenCategoryModal = () => {
        categoryForm.setFieldsValue({
            name: category.name,
            description: category.description,
        });
        setIsCategoryModalOpen(true);
    };

    const handleUpdateCategory = async () => {
        const values = await categoryForm.validateFields();
        await updateCategoryMutation({
            id: category.id,
            body: values,
        });
    };

    const handleDeleteCategory = () => {
        Modal.confirm({
            title: t("serviceCategorySection.deleteCategoryConfirmTitle"),
            content: t("serviceCategorySection.deleteCategoryConfirmContent"),
            okText: t("serviceCategorySection.delete"),
            okType: "danger",
            cancelText: t("serviceCategorySection.cancel"),
            onOk: async () => {
                await deleteCategoryMutation(category.id);
            },
        });
    };

    const handleServiceClick = (serviceId: number) => {
        setEditingServiceId(serviceId);
        setIsEditServiceModalOpen(true);
    };

    const formatPrice = (priceInGrosz: number) => {
        return `${(priceInGrosz / 100).toFixed(2)} ${currencySymbol(currentBranch?.currency)}`;
    };

    const hasScroll = services.length > 5;

    return (
        <>
            <CategoryContainer>
                <CategoryHeader>
                    <CategoryTitleRow>
                        <CategoryTitle onClick={handleOpenCategoryModal}>
                            {category.name}
                        </CategoryTitle>
                        <Tooltip title={t("serviceCategorySection.editCategoryTooltip")}>
                            <ArrowButton
                                type="text"
                                icon={<EditOutlined />}
                                onClick={handleOpenCategoryModal}
                                className="edit-button"
                            />
                        </Tooltip>
                    </CategoryTitleRow>
                    <CategoryActions>
                        <Button
                            type="default"
                            size="middle"
                            icon={<PlusOutlined />}
                            onClick={handleAddService}
                        >
                            <AddButtonText>
                                <span className="desktop-text">{t("serviceCategorySection.addService")}</span>
                                <span className="mobile-text">{t("serviceCategorySection.service")}</span>
                            </AddButtonText>
                        </Button>
                        <OrderButtonsGroup>
                            <Tooltip title={t("serviceCategorySection.moveCategoryUp")}>
                                <ArrowButton
                                    type="text"
                                    icon={<CaretUpOutlined />}
                                    onClick={onMoveUp}
                                    disabled={!canMoveUp}
                                />
                            </Tooltip>
                            <Tooltip title={t("serviceCategorySection.moveCategoryDown")}>
                                <ArrowButton
                                    type="text"
                                    icon={<CaretDownOutlined />}
                                    onClick={onMoveDown}
                                    disabled={!canMoveDown}
                                />
                            </Tooltip>
                        </OrderButtonsGroup>
                    </CategoryActions>
                </CategoryHeader>

                <ServicesList ref={servicesListRef} hasScroll={hasScroll}>
                    {services.map((service, index) => (
                        <ServiceRow
                            key={service.id}
                            onClick={() => handleServiceClick(service.id)}
                        >
                            <ServiceInfo>
                                {service.imageUrlS3 ? (
                                    <ServiceImage
                                        src={`${service.imageUrlS3}?t=${new Date().getTime()}`}
                                        alt={service.name}
                                    />
                                ) : (
                                    <ServiceImagePlaceholder />
                                )}
                                <ServiceName>{service.name}</ServiceName>
                                {service.price > 0 && (
                                    <ServicePrice>{formatPrice(service.price)}</ServicePrice>
                                )}
                            </ServiceInfo>

                            <ServiceActions onClick={(e) => e.stopPropagation()}>
                                <ServiceOrderButtonsGroup>
                                    <Tooltip title={t("serviceCategorySection.moveUp")}>
                                        <ArrowButton
                                            type="text"
                                            icon={<CaretUpOutlined />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleMoveServiceUp(service.id);
                                            }}
                                            disabled={index === 0}
                                        />
                                    </Tooltip>

                                    <Tooltip title={t("serviceCategorySection.moveDown")}>
                                        <ArrowButton
                                            type="text"
                                            icon={<CaretDownOutlined />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleMoveServiceDown(service.id);
                                            }}
                                            disabled={index === services.length - 1}
                                        />
                                    </Tooltip>
                                </ServiceOrderButtonsGroup>
                            </ServiceActions>
                        </ServiceRow>
                    ))}

                    {services.length === 0 && (
                        <ServiceRow onClick={handleAddService}>
                            <ServiceInfo>
                                <ServiceName style={{ color: colors.textSecondary }}>
                                    {t("serviceCategorySection.noServicesInCategory")}
                                </ServiceName>
                            </ServiceInfo>
                        </ServiceRow>
                    )}
                </ServicesList>
            </CategoryContainer>

            {/* Edit category modal */}
            <Modal
                title={t("serviceCategorySection.editCategoryModalTitle")}
                open={isCategoryModalOpen}
                onOk={handleUpdateCategory}
                onCancel={() => setIsCategoryModalOpen(false)}
                okText={t("serviceCategorySection.save")}
                cancelText={t("serviceCategorySection.cancel")}
                footer={[
                    <Button key="delete" danger onClick={handleDeleteCategory}>
                        {t("serviceCategorySection.deleteCategory")}
                    </Button>,
                    <Button key="cancel" onClick={() => setIsCategoryModalOpen(false)}>
                        {t("serviceCategorySection.cancel")}
                    </Button>,
                    <Button key="save" type="primary" onClick={handleUpdateCategory}>
                        {t("serviceCategorySection.save")}
                    </Button>,
                ]}
            >
                <Form form={categoryForm} layout="vertical">
                    <Form.Item
                        name="name"
                        label={t("serviceCategorySection.categoryNameLabel")}
                        rules={[{ required: true, message: t("serviceCategorySection.categoryNameRequired") }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item name="description" label={t("serviceCategorySection.descriptionLabel")}>
                        <Input.TextArea placeholder={t("serviceCategorySection.categoryDescriptionPlaceholder")} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Add service modal */}
            <Modal
                title={t("serviceCategorySection.addServiceModalTitle")}
                open={isAddServiceModalOpen}
                onOk={handleAddServiceSubmit}
                onCancel={() => {
                    addServiceForm.resetFields();
                    setAddServicePrice(0);
                    setIsAddServiceModalOpen(false);
                }}
                okText={t("serviceCategorySection.create")}
                cancelText={t("serviceCategorySection.cancel")}
                confirmLoading={isCreatingService}
            >
                <Form form={addServiceForm} layout="vertical">
                    <Form.Item
                        name="name"
                        label={t("serviceCategorySection.serviceNameLabel")}
                        rules={[{ required: true, message: t("serviceCategorySection.serviceNameRequired") }]}
                    >
                        <Input placeholder={t("serviceCategorySection.serviceNamePlaceholder")} autoFocus />
                    </Form.Item>
                    <Form.Item label={t("serviceCategorySection.priceLabel")}>
                        <PriceInput
                            value={addServicePrice}
                            onChange={setAddServicePrice}
                            editMode={true}
                            width="100%"
                            currency={currentBranch?.currency}
                        />
                    </Form.Item>
                </Form>
            </Modal>
            <EditServiceModal
                open={isEditServiceModalOpen}
                serviceId={editingServiceId}
                categoryId={category.id}
                onCancel={() => {
                    setIsEditServiceModalOpen(false);
                    setEditingServiceId(null);
                }}
                onSuccess={() => {
                    setIsEditServiceModalOpen(false);
                    setEditingServiceId(null);
                    queryClient.invalidateQueries([`/services-category-${category.id}`]);
                }}
            />
        </>
    );
};
